import React, { useMemo } from 'react'
import GradingState from '@/components/organisms/Grading/GradingState'

interface GradingVideoQuizProps {
  type?: 'scoreCard' | 'gradingCard'
  activity?: any
  responseJson?: string
}

const normalizeSelectionList = (values: number[]): number[] => {
  const unique = Array.from(new Set(values));
  unique.sort((a, b) => a - b);
  return unique;
};

const normalizeAnswerArray = (value: any): number[] => {
  const bucket: number[] = [];
  if (Array.isArray(value)) {
    value.forEach((entry) => {
      const numeric = typeof entry === 'number' ? entry : Number(entry);
      if (Number.isFinite(numeric)) {
        bucket.push(Math.trunc(numeric));
      }
    });
  } else if (typeof value === 'number' && Number.isFinite(value)) {
    bucket.push(Math.trunc(value));
  } else if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      bucket.push(Math.trunc(parsed));
    }
  }
  return normalizeSelectionList(bucket);
};

const convertCorrectAnswers = (answer: any): number[] =>
  normalizeAnswerArray(answer).map((idx) => idx + 1);

const normalizeStudentAnswers = (answers?: Record<string | number, any>): Record<number, number[]> => {
  if (!answers) {
    return {};
  }
  const normalized: Record<number, number[]> = {};
  Object.entries(answers).forEach(([key, value]) => {
    const idx = Number(key);
    if (Number.isNaN(idx)) {
      return;
    }
    const normalizedValue = normalizeAnswerArray(value);
    if (normalizedValue.length > 0) {
      normalized[idx] = normalizedValue;
    }
  });
  return normalized;
};

const selectionsMatch = (selected: number[] | undefined, correct: number[]): boolean => {
  if (!selected || selected.length === 0 || correct.length === 0) {
    return false;
  }
  const normalizedSelected = normalizeSelectionList(selected);
  const normalizedCorrect = normalizeSelectionList(correct);
  if (normalizedSelected.length !== normalizedCorrect.length) {
    return false;
  }
  return normalizedSelected.every((value, idx) => value === normalizedCorrect[idx]);
};

const GradingVideoQuiz = ({ type = 'scoreCard', activity, responseJson }: GradingVideoQuizProps) => {
  // 실제 데이터 파싱
  const { videoUrl, quizzes, studentAnswers } = useMemo(() => {
    if (!activity) {
      // 더미 데이터
      return {
        videoUrl: '',
        quizzes: [
          {
            id: 1,
            question: 'What is the main topic of the video?',
            choices: ['Option A', 'Option B', 'Option C', 'Option D'],
            correctOptionIds: [1],
            reason: 'This is the correct answer'
          }
        ],
        studentAnswers: {}
      };
    }

    // configJson 또는 items에서 데이터 가져오기
    let parsedVideoUrl = '';
    let parsedQuizzes: any[] = [];

    try {
      // DICTATION 타입의 경우 configJson에 데이터가 있음
      const configJson = activity?.configJson;
      if (configJson) {
        const config = typeof configJson === 'string' ? JSON.parse(configJson) : configJson;
        const hasVideoUrl = !!config?.videoUrl;
        if (config?.videoQuizEnabled === true || hasVideoUrl) {
          parsedVideoUrl = config.videoUrl || '';
          parsedQuizzes = Array.isArray(config.quizzes) ? config.quizzes : [];
        }
      }

      // configJson에 데이터가 없으면 items에서 가져오기 (VIDEO_QUIZ 타입)
      if (!parsedVideoUrl && activity?.items && activity.items.length > 0) {
        const activityData = activity.items[0];
        const jsonData = activityData.contentJson || '{}';
        const parsedData = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
        parsedVideoUrl = parsedData.videoUrl || '';
        parsedQuizzes = parsedData.quizzes || [];
      }
    } catch (e) {
      console.error('동영상 퀴즈 데이터 파싱 에러:', e);
    }

    // responseJson 파싱하여 학생 답변 가져오기
    let answers: Record<number, number[]> = {};
    if (responseJson) {
      try {
        const response = JSON.parse(responseJson);
        
        // responseJson이 단일 응답 객체인 경우 (flatItem의 globalIndex에 해당하는 응답)
        if (response && response.selectedAnswers) {
          answers = normalizeStudentAnswers(response.selectedAnswers);
          console.log('동영상 퀴즈 응답:', answers);
        }
      } catch (e) {
        console.error('responseJson 파싱 실패:', e);
      }
    }

    const normalizedQuizzes = (parsedQuizzes || []).map((quiz: any, idx: number) => {
      const providedCorrectIds = Array.isArray(quiz?.correctOptionIds)
        ? normalizeSelectionList(quiz.correctOptionIds)
        : null;
      return {
        id: quiz?.id ?? idx + 1,
        question: quiz?.question || '',
        choices: Array.isArray(quiz?.choices) ? quiz.choices : [],
        correctOptionIds: providedCorrectIds && providedCorrectIds.length > 0
          ? providedCorrectIds
          : convertCorrectAnswers(quiz?.answer),
        reason: quiz?.reason || ''
      };
    });

    return {
      videoUrl: parsedVideoUrl,
      quizzes: normalizedQuizzes,
      studentAnswers: answers
    };
  }, [activity, responseJson]);

  // YouTube 또는 Vimeo URL을 embed URL로 변환
  const getEmbedUrl = (url: string) => {
    if (!url) return '';

    // YouTube
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.includes('youtu.be')
        ? url.split('youtu.be/')[1]?.split('?')[0]
        : new URLSearchParams(new URL(url).search).get('v');
      return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
    }

    // Vimeo
    if (url.includes('vimeo.com')) {
      const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
      return videoId ? `https://player.vimeo.com/video/${videoId}` : url;
    }

    return url;
  };

  const embedUrl = getEmbedUrl(videoUrl);

  // 퀴즈별 상태 계산
  const getQuizStatus = (quizIndex: number, correctAnswers: number[]) => {
    const studentAnswer = studentAnswers[quizIndex + 1]; // 1-based index
    
    if (!studentAnswer || studentAnswer.length === 0) {
      return 'grading' as const;
    }
    
    const isCorrect = selectionsMatch(studentAnswer, correctAnswers);
    return isCorrect ? 'correct' as const : 'incorrect' as const;
  };

  return (
    <div>
      <div className='flex flex-col gap-2'>
        <p className='text-lg font-bold'>동영상 퀴즈</p>
        
        {/* 동영상 영역 */}
        <div className='relative rounded-2xl border border-gray-100 overflow-hidden'>
          {embedUrl ? (
            <iframe
              src={embedUrl}
              className='w-full rounded-2xl aspect-video'
              allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
              allowFullScreen
            />
          ) : (
            <div className="flex items-center justify-center w-full rounded-2xl aspect-video bg-gray-100 text-gray-400 font-bold text-lg">
              동영상이 없습니다
            </div>
          )}
        </div>

        {/* 퀴즈 목록 */}
        {quizzes.length === 0 ? (
          <div className='p-5 bg-gray-50 rounded-2xl text-center'>
            <p className='text-sm text-gray-500'>등록된 퀴즈가 없습니다.</p>
          </div>
        ) : (
          <div className='space-y-4'>
            {quizzes.map((quiz: any, quizIndex: number) => {
              const correctAnswers = Array.isArray(quiz.correctOptionIds) ? quiz.correctOptionIds : [];
              const status = getQuizStatus(quizIndex, correctAnswers);
              const studentAnswer = studentAnswers[quizIndex + 1] || [];

              return (
                <div key={quizIndex} className='p-4 bg-gray-50 rounded-2xl'>
                  {/* 질문 */}
                  <div className='flex justify-between items-start mb-3'>
                    <p className='text-base font-bold flex-1'>
                      Q{quizIndex + 1}. {quiz.question}
                    </p>
                    <GradingState status={status} />
                  </div>

                  {/* 선택지 */}
                  <div className='space-y-2 mb-3'>
                    {(quiz.choices || []).map((choice: string, choiceIndex: number) => {
                      const optionId = choiceIndex + 1;
                      const isStudentChoice = studentAnswer.includes(optionId);
                      const isCorrectChoice = correctAnswers.includes(optionId);
                      
                      let bgColor = 'bg-white';
                      let borderColor = 'border-gray-200';
                      let textColor = 'text-gray-700';

                      if (isStudentChoice && isCorrectChoice) {
                        // 학생이 선택한 답이 정답인 경우
                        bgColor = 'bg-green-50';
                        borderColor = 'border-green-500';
                        textColor = 'text-green-700';
                      } else if (isStudentChoice && !isCorrectChoice) {
                        // 학생이 선택한 답이 오답인 경우
                        bgColor = 'bg-red-50';
                        borderColor = 'border-red-500';
                        textColor = 'text-red-700';
                      } else if (status === 'incorrect' && !isStudentChoice && isCorrectChoice) {
                        // [QA Fix] Row 393 - 오답일 때만 정답 강조
                        bgColor = 'bg-green-50';
                        borderColor = 'border-green-300';
                        textColor = 'text-green-600';
                      }

                      return (
                        <div 
                          key={choiceIndex}
                          className={`p-3 rounded-lg border-2 ${bgColor} ${borderColor} transition-colors`}
                        >
                          <div className='flex items-center gap-2'>
                            <span className={`text-sm font-medium ${textColor}`}>
                              {String.fromCharCode(65 + choiceIndex)}.
                            </span>
                            <span className={`text-sm ${textColor}`}>
                              {choice}
                            </span>
                            {isStudentChoice && (
                              <span className='ml-auto text-xs font-bold text-gray-500'>
                                (학생 답변)
                              </span>
                            )}
                            {status === 'incorrect' && !isStudentChoice && isCorrectChoice && (
                              <span className='ml-auto text-xs font-bold text-green-600'>
                                (정답)
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* 해설 (오답인 경우에만 표시) */}
                  {status === 'incorrect' && quiz.reason && (
                    <div className='p-3 bg-blue-50 border border-blue-200 rounded-lg'>
                      <p className='text-xs font-bold text-blue-700 mb-1'>해설</p>
                      <p className='text-sm text-blue-600'>{quiz.reason}</p>
                    </div>
                  )}

                  {/* 학생이 아직 답변하지 않은 경우 */}
                  {status === 'grading' && (
                    <div className='p-3 bg-orange-50 border border-orange-200 rounded-lg'>
                      <p className='text-sm text-orange-600'>아직 답변하지 않았습니다.</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default GradingVideoQuiz

